import { useGetContext } from '@api/lendingAPI/lapiHooks';
import {
    fetchGetFundingRequirementsCreditFacility,
    fetchGetStateByStateKey,
    GetFundingRequirementsCreditFacilityResponse,
    GetStateResponse
} from '@iwoca/lapi-client/edge';
import { useQuery } from '@tanstack/react-query';
import _ from 'lodash';

import { useStateKey } from '../../../../../iwQueryClient/useStateKey';
import { getCurrentLocation } from '../../../../../iwRouter/HistoryProvider';
import { getQueryParameter } from '../../../../../iwRouter/LocationHelper';
import {
    TDeserialisedPerson,
    TResolutionMethodsSerialisedStatuses,
    TSerialisedPeople
} from '../../../../modals/idCheckVerificationModal/IdCheckVerificationModal.types';
import { hasIDVerseSplitTest } from '../../../../modals/idCheckVerificationModal/IdCheckVerificationModalHelper';

type TIdCheckResponse = {
    // All people whose id-check is outstanding (applicant + any guarantors),
    // outstanding-first. The UI currently renders only the first; the list is
    // ready so multi-person can be switched on without reworking the hook.
    people: TDeserialisedPerson[];
    id_verification_provider_name: 'Entrust (Onfido)' | 'IDVerse';
};

export const useGetPersonToVerify = (): {
    idCheckResponse: TIdCheckResponse | undefined;
} => {
    const stateKey = useStateKey();
    const location = getCurrentLocation();
    const offerId = getQueryParameter(location, 'offerId');
    const amount = getQueryParameter(location, 'amount');

    const { data: customerState } = useQuery({
        queryKey: ['customer_state', stateKey],
        queryFn: () => fetchGetStateByStateKey({ stateKey }),
        suspense: true
    });

    // Shares the cache entry with useGetFundingRequirements (same queryKey),
    // but selects the raw per-person id-check items rather than the deserialised list.
    const { data: idCheckItems } = useQuery({
        queryKey: [
            'fetchGetFundingRequirementsCreditFacility',
            stateKey,
            offerId,
            amount
        ],
        queryFn: () =>
            fetchGetFundingRequirementsCreditFacility({
                stateKey,
                offerId: offerId!,
                query: { cash_amount: Number(amount) }
            }),
        enabled: Boolean(offerId) && Boolean(amount),
        select: selectIdCheckItems,
        suspense: true,
        // Poll while a check is mid-verification so the view auto-advances to
        // complete once the backend settles; stop once nothing is pending.
        refetchInterval: (idCheckItems) =>
            hasPendingIdCheck(idCheckItems) ? PENDING_POLL_INTERVAL_MS : false
    });

    const { data: site_context } = useGetContext();

    if (!customerState?.data?.application?.people) {
        throw new Error('Application people data is required');
    }

    const idCheckResponse = deserialiseIdCheckResponse(
        customerState.data,
        idCheckItems,
        hasIDVerseSplitTest(site_context)
    );

    return { idCheckResponse };
};

type TIdCheckItems = {
    onfidoStatuses: TResolutionMethodsSerialisedStatuses;
    onfidoVideoStatuses: TResolutionMethodsSerialisedStatuses;
    idverseStatuses: TResolutionMethodsSerialisedStatuses;
    hasIdCheck: boolean;
};

const PENDING_POLL_INTERVAL_MS = 2000;

// True while any id-check item is still verifying (pending).
const hasPendingIdCheck = (idCheckItems: TIdCheckItems | undefined): boolean =>
    [
        ...(idCheckItems?.onfidoStatuses ?? []),
        ...(idCheckItems?.onfidoVideoStatuses ?? []),
        ...(idCheckItems?.idverseStatuses ?? [])
    ].some((item) => item.status === 'pending');

const getItemsFor = (
    requirements: GetFundingRequirementsCreditFacilityResponse['data'],
    method: 'onfido_check' | 'onfido_video_check' | 'idverse_check'
): TResolutionMethodsSerialisedStatuses => {
    const requirement = _.find(requirements, {
        recommended_resolution_method: method
    });
    return _.find(requirement?.resolution_methods, { type: method })?.items;
};

const selectIdCheckItems = (
    response: GetFundingRequirementsCreditFacilityResponse
): TIdCheckItems => {
    const onfidoStatuses = getItemsFor(response.data, 'onfido_check');
    const onfidoVideoStatuses = getItemsFor(
        response.data,
        'onfido_video_check'
    );
    const idverseStatuses = getItemsFor(response.data, 'idverse_check');

    return {
        onfidoStatuses,
        onfidoVideoStatuses,
        idverseStatuses,
        hasIdCheck: Boolean(
            onfidoStatuses || onfidoVideoStatuses || idverseStatuses
        )
    };
};

const deserialiseIdCheckResponse = (
    customerState: GetStateResponse['data'],
    idCheckItems: TIdCheckItems | undefined,
    isIDVerse: boolean
): TIdCheckResponse | undefined => {
    if (!idCheckItems?.hasIdCheck) return undefined;

    // The backend keys id-check items by person uid, one per person needing a
    // check (applicant + any guarantors). Resolve each to a person, outstanding first.
    const people = getPersonUidsToVerify(idCheckItems)
        .map((uid) => _.find(customerState.application!.people, { uid }))
        .filter((person): person is NonNullable<typeof person> =>
            Boolean(person)
        )
        .map((person) => deserialisePerson(person, idCheckItems));

    if (people.length === 0) return undefined;

    return {
        people,
        id_verification_provider_name: isIDVerse
            ? 'IDVerse'
            : 'Entrust (Onfido)'
    };
};

// Unique person uids across all id-check methods, outstanding (non-completed) first.
const getPersonUidsToVerify = ({
    onfidoStatuses,
    onfidoVideoStatuses,
    idverseStatuses
}: TIdCheckItems): string[] => {
    const items = [
        ...(onfidoStatuses ?? []),
        ...(onfidoVideoStatuses ?? []),
        ...(idverseStatuses ?? [])
    ];
    const uids = _.chain(items)
        .filter((item) => Boolean(item.name))
        .sortBy((item) => (item.status === 'completed' ? 1 : 0))
        .map((item) => item.name!)
        .uniq()
        .value();
    return uids;
};

const deserialisePerson = (
    person: NonNullable<TSerialisedPeople>[number],
    idCheckItems: TIdCheckItems
): TDeserialisedPerson => ({
    uid: person.uid,
    title: person.title!,
    firstName: person.first_name!,
    lastName: person.last_name!,
    roles: person.roles,
    phones: deserialisePhones(person.phones),
    idCheckStatus: getIdCheckStatus(idCheckItems, person.uid),
    addPhoneNumberPending: false
});

const getIdCheckStatus = (
    { onfidoStatuses, onfidoVideoStatuses, idverseStatuses }: TIdCheckItems,
    uid: string
) => {
    const onfidoStatus = _.find(onfidoStatuses, { name: uid });
    const onfidoVideoStatus = _.find(onfidoVideoStatuses, { name: uid });
    const idverseStatus = _.find(idverseStatuses, { name: uid });

    if (!onfidoStatus && !idverseStatus) {
        return 'Not started yet';
    }

    if (
        (onfidoStatus?.status === 'completed' &&
            (onfidoVideoStatus?.status ?? 'completed') === 'completed') ||
        idverseStatus?.status === 'completed'
    ) {
        return 'Verified';
    }

    if (
        onfidoStatus?.status === 'pending' ||
        onfidoVideoStatus?.status === 'pending' ||
        idverseStatus?.status === 'pending'
    ) {
        return 'Verifying your check';
    }

    if (
        onfidoStatus?.status === 'error' ||
        onfidoVideoStatus?.status === 'error' ||
        idverseStatus?.status === 'error'
    ) {
        return 'Something went wrong';
    }

    return 'Not started yet';
};

const deserialisePhones = (
    phones: NonNullable<TSerialisedPeople>[number]['phones']
) => {
    return _.map(phones, (phone) => ({
        uid: phone.uid,
        label: phone.number,
        value: phone.number
    }));
};
